import Select from "react-select";
import NumberFormat from "react-number-format";
import { Row, Col, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { numberWithCommas } from "../../utils/customFunction";
import { useTheme } from "../../contexts/ThemeContext";
import { useInvestmentTransactionContext } from "../../contexts/VpsTransaction";

export default function AllocationChange() {
  const {
    fields,
    errors,
    viewMode,
    customStyles,
    coaFunds,
    fromScheme,
    toScheme,
    schemeOptions,
    currentSubFundsBlock,
    typeVPS,
    currAmount,
    updateBlockInstrumentDetail,
    showApproval,
    handleFromSchemeChange,
    handleToSchemeChange,
    handleToFundChange,
    handleFromFundChange, // NEW
    handleFromUnitChange,
    handleCoaAmountChange,
    instrumentTypes,
    handleFromPctChange,
    addCoaFundRow,
    removeCoaFundRow,
    setCoaFunds,
    parentFundChildFunds,
  } = useInvestmentTransactionContext();

  const { theme } = useTheme();

  if (!["changeofallocation", "view"].includes(viewMode)) return null;
  if (
    fields.txnr_type &&
    !["changeofallocation", "view"].includes(fields.txnr_type)
  )
    return null;

  const isView = viewMode === "view";

  // Build from-fund options from unitholder's currentSubFundsBlock
  // Already-used from_funds are excluded (except the current row's own fund)
  const getFromFundOptions = (currentItemId?: string) => {
    const usedUnitsByFund = coaFunds
      .filter((f) => f.id !== currentItemId && f.from_fund)
      .reduce((acc: any, f) => {
        acc[f.from_fund] = (acc[f.from_fund] || 0) + (f.from_fund_unit || 0);
        return acc;
      }, {});

    return (
      currentSubFundsBlock
        ?.flatMap((b: any) => b.subFunds || [])
        ?.map((s: any) => {
          const adjustedHolding =
            parseFloat(s.holding || 0) - (usedUnitsByFund[s.fund_code] || 0);
          return {
            value: s.fund_code,
            label: `${s.name || s.fund_code} (${s.fund_code}) — holding: ${numberWithCommas(
              adjustedHolding.toFixed(4),
            )}`,
            holding: adjustedHolding,
            fund_code: s.fund_code,
            name: s.name,
          };
        }) || []
    );
  };

  // Build to-fund options: all subFunds except the selected from_fund
  const getToFundOptions = (fromFundCode: string) => {
    if (toScheme && toScheme !== fromScheme && coaFunds.length === 1) {
      return (
        parentFundChildFunds
          ?.filter((s: any) => s.symbol_code !== fromFundCode)
          ?.map((s: any) => ({
            value: s.symbol_code,
            label: `${s.fund_name || s.name || s.symbol_code} (${s.symbol_code})`,
            fund_code: s.symbol_code,
            name: s.fund_name || s.name,
          })) || []
      );
    }

    return (
      currentSubFundsBlock
        ?.flatMap((b: any) => b.subFunds || [])
        ?.filter((s: any) => s.fund_code !== fromFundCode)
        ?.map((s: any) => ({
          value: s.fund_code,
          label: `${s.name || s.fund_code} (${s.fund_code})`,
          fund_code: s.fund_code,
          name: s.name,
        })) || []
    );
  };

  const getHolding = (fundCode: string, currentItemId?: string) => {
    const baseHolding =
      currentSubFundsBlock
        ?.flatMap((b: any) => b.subFunds || [])
        ?.find((s: any) => s.fund_code === fundCode)?.holding || 0;

    const usedUnits = coaFunds
      .filter((f) => f.id !== currentItemId && f.from_fund === fundCode)
      .reduce((sum, f) => sum + (f.from_fund_unit || 0), 0);

    return parseFloat(baseHolding) - usedUnits;
  };

  const cardBg = theme === "dark" ? "#2f343c0" : "#f8f9fa";

  return (
    <>
      {/* Current systematic investment (read-only summary) */}
      {fields.amc_code && fields.folio_no && (
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Current systematic investment</CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="form-group">
                      <label>Allocation scheme</label>
                      <input
                        disabled
                        className="form-control"
                        value={typeVPS}
                      />
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <label>Investment amount</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">PKR</span>
                        </div>
                        <NumberFormat
                          thousandSeparator
                          value={currentSubFundsBlock[0].total_amount}
                          className="form-control"
                          disabled
                        />
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Sub-funds table */}
                {currentSubFundsBlock?.map((block: any, bi: number) => (
                  <div key={block.id || bi} className="mt-3">
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>Fund name</th>
                            <th>Holding (units)</th>
                            <th>Allocation %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {block.subFunds?.map((sf: any, si: number) => (
                            <tr key={si}>
                              <td>{sf.name || sf.fund_code}</td>
                              <td>
                                {numberWithCommas(
                                  parseFloat(sf.holding || 0).toFixed(4),
                                )}
                              </td>
                              <td>{sf.proportion}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Scheme selection — OPTIONAL */}
      {((viewMode === "view" && toScheme) ||
        (viewMode !== "view" && fields.folio_no)) && (
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">
                  Scheme change{" "}
                  <small
                    className="text-muted font-weight-normal ml-2"
                    style={{ fontSize: "12px" }}
                  >
                    optional — leave blank to reallocate within current scheme
                  </small>
                </CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="form-group">
                      <label>Current scheme</label>
                      <input
                        className="form-control"
                        value={fromScheme}
                        disabled
                        readOnly
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-group">
                      <label>
                        Target scheme{" "}
                        <small className="text-muted">(optional)</small>
                      </label>
                      {(viewMode as string) === "view" ? (
                        <input
                          value={toScheme}
                          disabled
                          className="form-control"
                        />
                      ) : (
                        <Select
                          options={[
                            {
                              value: "",
                              label: "— same scheme (reallocate within) —",
                            },
                            ...schemeOptions,
                          ]}
                          value={
                            toScheme
                              ? schemeOptions.find(
                                  (s) => s.value === toScheme,
                                ) || null
                              : {
                                  value: "",
                                  label: "— same scheme (reallocate within) —",
                                }
                          }
                          onChange={(opt) =>
                            handleToSchemeChange(opt?.value || "")
                          }
                          placeholder="Select target scheme (optional)"
                          isDisabled={isView}
                          styles={customStyles}
                          isClearable
                        />
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Fund transfer details — always shown once unitholder is selected */}
      {fields.amc_code && fields.folio_no && (
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h4" className="mb-0">
                  Fund transfer details
                </CardTitle>
                {!isView && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={addCoaFundRow}
                  >
                    <i className="fa fa-plus mr-1" /> Add fund transfer
                  </button>
                )}
              </CardHeader>
              <CardBody>
                {coaFunds.length === 0 && (
                  <div className="text-center text-muted py-4">
                    No fund transfers configured. Click "Add fund transfer" to
                    begin.
                  </div>
                )}

                {coaFunds.map((item, index) => {
                  const holding = getHolding(item.from_fund, item.id);
                  const maxAmount =
                    parseFloat(String(holding)) * item.from_fund_nav;
                  const fromFundOptions = getFromFundOptions(item.id);
                  const toFundOptions = getToFundOptions(item.from_fund);

                  const selectedFromFund = item.from_fund
                    ? {
                        value: item.from_fund,
                        label: currentSubFundsBlock
                          ?.flatMap((b: any) => b.subFunds || [])
                          ?.find((s: any) => s.fund_code === item.from_fund)
                          ?.name
                          ? `${currentSubFundsBlock.flatMap((b: any) => b.subFunds || []).find((s: any) => s.fund_code === item.from_fund)?.name} (${item.from_fund}) — holding: ${numberWithCommas(parseFloat(String(holding)).toFixed(4))}`
                          : item.from_fund,
                      }
                    : null;

                  const selectedToFund = item.to_fund
                    ? toFundOptions.find((f) => f.value === item.to_fund) || {
                        value: item.to_fund,
                        label: item.to_fund,
                      }
                    : null;

                  return (
                    <div
                      key={item.id}
                      className="mb-4 p-3"
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        backgroundColor: cardBg,
                      }}
                    >
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Transfer #{index + 1}</h5>
                        {!isView && coaFunds.length > 1 && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeCoaFundRow(item.id)}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        )}
                      </div>

                      {/* From / To Fund selectors */}
                      <Row>
                        <Col md={3}>
                          <div className="form-group">
                            <label>
                              From fund <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={fromFundOptions}
                              value={selectedFromFund}
                              onChange={(opt) =>
                                handleFromFundChange(opt?.value || "", item.id)
                              }
                              placeholder="Select source fund"
                              isDisabled={isView}
                              styles={customStyles}
                            />
                            {item.from_fund && (
                              <small className="text-muted">
                                Available:{" "}
                                {numberWithCommas(
                                  parseFloat(String(holding)).toFixed(4),
                                )}{" "}
                                units
                              </small>
                            )}
                            {errors[`coa_${item.id}_fromfund`] && (
                              <small className="text-danger d-block">
                                {errors[`coa_${item.id}_fromfund`]}
                              </small>
                            )}
                          </div>
                        </Col>
                        {/* Account selectors */}
                        <Col md={3}>
                          <div className="form-group">
                            <label>From account</label>
                            <Select
                              options={item.from_accounts}
                              value={item.selected_from_account}
                              onChange={(sel) =>
                                setCoaFunds((prev) =>
                                  prev.map((f) =>
                                    f.id === item.id
                                      ? {
                                          ...f,
                                          selected_from_account: sel,
                                          from_fund_acc: sel?.account_no || "",
                                          from_fund_acc_title:
                                            sel?.account_title || "",
                                        }
                                      : f,
                                  ),
                                )
                              }
                              isDisabled={isView || !item.from_fund}
                              placeholder={
                                item.from_fund
                                  ? "Select account"
                                  : "Select from fund first"
                              }
                              styles={customStyles}
                            />
                            {errors[`coa_${item.id}_fromacc`] && (
                              <small className="text-danger d-block">
                                {errors[`coa_${item.id}_fromacc`]}
                              </small>
                            )}
                          </div>
                        </Col>

                        <Col md={3}>
                          <div className="form-group">
                            <label>
                              To fund <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={toFundOptions}
                              value={selectedToFund}
                              onChange={(opt) =>
                                handleToFundChange(opt?.value || "", item.id)
                              }
                              placeholder={
                                item.from_fund
                                  ? "Select target fund"
                                  : "Select from fund first"
                              }
                              isDisabled={isView || !item.from_fund}
                              styles={customStyles}
                            />
                            {errors[`coa_${item.id}_tofund`] && (
                              <small className="text-danger d-block">
                                {errors[`coa_${item.id}_tofund`]}
                              </small>
                            )}
                          </div>
                        </Col>

                        <Col md={3}>
                          <div className="form-group">
                            <label>To account</label>
                            <Select
                              options={item.to_accounts}
                              value={item.selected_to_account}
                              onChange={(sel) =>
                                setCoaFunds((prev) =>
                                  prev.map((f) =>
                                    f.id === item.id
                                      ? {
                                          ...f,
                                          selected_to_account: sel,
                                          to_fund_acc: sel?.account_no || "",
                                          to_fund_acc_title:
                                            sel?.account_title || "",
                                        }
                                      : f,
                                  ),
                                )
                              }
                              isDisabled={isView || !item.to_fund}
                              placeholder={
                                item.to_fund
                                  ? "Select account"
                                  : "Select to fund first"
                              }
                              styles={customStyles}
                            />
                            {errors[`coa_${item.id}_toacc`] && (
                              <small className="text-danger d-block">
                                {errors[`coa_${item.id}_toacc`]}
                              </small>
                            )}
                          </div>
                        </Col>
                      </Row>

                      {/* NAV / Units / Amount row */}
                      <Row className="mt-2">
                        <Col md={3}>
                          <div className="form-group">
                            <label>Source NAV</label>
                            <div className="input-group">
                              <div className="input-group-prepend">
                                <span className="input-group-text">PKR</span>
                              </div>
                              <input
                                className="form-control"
                                value={
                                  item.from_fund_nav
                                    ? item.from_fund_nav.toFixed(4)
                                    : "—"
                                }
                                disabled
                                readOnly
                              />
                            </div>
                          </div>
                        </Col>

                        <Col md={3}>
                          <div className="form-group">
                            <label>Units to transfer </label>
                            <NumberFormat
                              thousandSeparator
                              value={item.from_fund_unit || ""}
                              onValueChange={({ floatValue }) =>
                                handleFromUnitChange(item.id, floatValue || 0)
                              }
                              className={`form-control ${errors[`coa_${item.id}_units`] ? "is-invalid" : ""}`}
                              disabled={isView || !item.from_fund}
                              decimalScale={4}
                              allowNegative={false}
                              placeholder="0.0000"
                            />
                            {item.from_fund && (
                              <small className="text-muted">
                                Max:{" "}
                                {numberWithCommas(
                                  parseFloat(String(holding)).toFixed(4),
                                )}
                              </small>
                            )}
                            {errors[`coa_${item.id}_units`] && (
                              <div className="invalid-feedback">
                                {errors[`coa_${item.id}_units`]}
                              </div>
                            )}
                          </div>
                        </Col>

                        <Col md={3}>
                          <div className="form-group">
                            <label>Units to receive</label>
                            <NumberFormat
                              thousandSeparator
                              value={
                                item.to_fund_unit
                                  ? item.to_fund_unit.toFixed(4)
                                  : "0.0000"
                              }
                              className="form-control bg-light"
                              disabled
                              readOnly
                              decimalScale={4}
                            />
                            {item.to_fund && item.to_fund_nav === 0 && (
                              <small className="text-warning d-block">
                                No NAV found for target fund on selected date
                              </small>
                            )}
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="form-group">
                            <label>Target NAV</label>
                            <div className="input-group">
                              <div className="input-group-prepend">
                                <span className="input-group-text">PKR</span>
                              </div>
                              <input
                                className="form-control"
                                value={
                                  item.to_fund_nav
                                    ? item.to_fund_nav.toFixed(4)
                                    : "—"
                                }
                                disabled
                                readOnly
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mt-2">
                        <Col md={3}>
                          <div className="form-group">
                            <label>
                              Instrument Type
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-control ${errors[`${index}_instrument_no`] ? "text-danger border-danger" : ""}`}
                              disabled={
                                viewMode === "view" &&
                                !(
                                  fields.txn_status === "COMPLIANCE" &&
                                  showApproval
                                )
                              }
                              value={item.instrument_type}
                              onChange={(e) =>
                                updateBlockInstrumentDetail(
                                  index,
                                  "instrument_type",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Select Type</option>
                              {instrumentTypes.map((t, i) => (
                                <option key={i} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          </div>
                          {errors[`${index}_instrument_type`] && (
                            <span className="text-danger">
                              {errors[`${index}_instrument_type`]}
                            </span>
                          )}
                        </Col>

                        <Col md={3}>
                          <div className="form-group">
                            <label>
                              Instrument Number
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors[`${index}_instrument_no`] ? "text-danger border-danger" : ""}`}
                              disabled={
                                viewMode === "view" &&
                                !(
                                  fields.txn_status === "COMPLIANCE" &&
                                  showApproval
                                )
                              }
                              value={item.instrument_no}
                              onChange={(e) =>
                                updateBlockInstrumentDetail(
                                  index,
                                  "instrument_no",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter instrument number"
                            />
                          </div>
                          {errors[`${index}_instrument_no`] && (
                            <span className="text-danger">
                              {errors[`${index}_instrument_no`]}
                            </span>
                          )}
                        </Col>

                        <Col md={3}>
                          <div className="form-group">
                            <label>Transfer amount</label>
                            <div className="input-group">
                              <div className="input-group-prepend">
                                <span className="input-group-text">PKR</span>
                              </div>
                              <NumberFormat
                                thousandSeparator
                                value={
                                  item.amount ? item.amount.toFixed(2) : "0.00"
                                }
                                className="form-control"
                                disabled
                                readOnly
                                decimalScale={2}
                              />
                            </div>
                            {item.from_fund && item.from_fund_nav > 0 && (
                              <small className="text-muted">
                                Max: PKR{" "}
                                {numberWithCommas(maxAmount.toFixed(2))}
                              </small>
                            )}
                          </div>
                        </Col>

                        <Col md={3}>
                          <div className="form-group">
                            <label>Transfer pct % </label>
                            <div className="input-group">
                              <NumberFormat
                                thousandSeparator
                                value={item.pct ? item.pct.toFixed(2) : "0.00"}
                                onValueChange={({ floatValue }) =>
                                  handleFromPctChange(item.id, floatValue || 0)
                                }
                                className="form-control"
                                decimalScale={2}
                              />
                            </div>
                            {errors[`coa_${item.id}_pct`] && (
                              <div className="invalid-feedback">
                                {errors[`coa_${item.id}_pct`]}
                              </div>
                            )}
                          </div>
                        </Col>

                        {/* <Col md={6}>
                          <div className="form-group">
                            <label>Load information</label>
                            <div
                              className="d-flex justify-content-between align-items-center form-control"
                              style={{ height: "auto", padding: "8px 12px" }}
                            >
                              <small className="text-muted">
                                Backend load:{" "}
                                <strong>{item.backend_load || 0}%</strong>
                              </small>
                              <small className="text-muted">
                                Frontend load:{" "}
                                <strong>{item.frontend_load || 0}%</strong>
                              </small>
                            </div>
                          </div>
                        </Col> */}
                      </Row>

                      {errors[`coa_${item.id}_value`] && (
                        <div className="alert alert-danger mt-2 py-2">
                          {errors[`coa_${item.id}_value`]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}
